import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getShow, getShowWatchProviders, submitShowRating, getDiscussions, createDiscussion, editDiscussion, deleteDiscussion } from "../api";
import { addToList, removeFromList, isInList, LIST_ITEMS } from "../utils/lists";
import { ratingLabels } from "../data/mockData";
import { useAuth } from "../context/AuthContext";

const NEPALI_PLATFORMS = [
  { name: "Cinemaghar", color: "#E8001C", url: "https://cinemaghar.com" },
  { name: "NetTV",      color: "#FF6600", url: "https://nettv.com.np"   },
];

const TMDB_KEY = process.env.REACT_APP_TMDB_KEY;

async function tmdbGet(path) {
  if (!TMDB_KEY) return null;
  try {
    const r = await fetch(`https://api.themoviedb.org/3${path}`, {
      headers: { Authorization: `Bearer ${TMDB_KEY}` },
    });
    return r.ok ? r.json() : null;
  } catch {
    return null;
  }
}

const TABS = [
  { key: "overview",    label: "overview"    },
  { key: "reviews",     label: "reviews"     },
  { key: "discussions", label: "discussions" },
  { key: "cast",        label: "cast & crew" },
  { key: "similar",     label: "similar"     },
];

const GUFF_TYPES = [
  { key: "battle",  icon: "⚔",  label: "Battle",         desc: "Two sides. One winner."         },
  { key: "hottake", icon: "🔥", label: "Hot Take",       desc: "Controversial opinion."         },
  { key: "scene",   icon: "🎬", label: "Scene Reaction", desc: "React to a specific moment."    },
  { key: "predict", icon: "🎯", label: "Prediction",     desc: "Guess how others rated it."     },
  { key: "spoiler", icon: "💀", label: "Spoiler Room",   desc: "Full spoilers, no limits."      },
];

// ─── Shared micro-components ──────────────────────────────────────────────────

function ProviderLogo({ p }) {
  const [failed, setFailed] = useState(false);
  return (
    <div title={p.name} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      {p.logo_url && !failed ? (
        <img src={p.logo_url} alt={p.name}
          style={{ width: 44, height: 44, borderRadius: 10, objectFit: "cover", border: "0.5px solid #252528" }}
          onError={() => setFailed(true)}
        />
      ) : (
        <div style={{ width: 44, height: 44, borderRadius: 10, background: "#252528", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#55555e" }}>
          {(p.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("")}
        </div>
      )}
      <span style={{ fontSize: 9, color: "#55555e", textAlign: "center", maxWidth: 52, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
    </div>
  );
}

function TabBar({ active, onSelect }) {
  return (
    <div style={{ display: "flex", overflowX: "auto" }}>
      {TABS.map(({ key, label }) => (
        <div key={key} onClick={() => onSelect(key)}
          style={{
            fontSize: 12, padding: "13px 20px", whiteSpace: "nowrap",
            color: active === key ? "#FF5C35" : "#55555e",
            borderBottom: active === key ? "2px solid #FF5C35" : "2px solid transparent",
            cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: "color 0.15s",
          }}
        >
          {label}
        </div>
      ))}
    </div>
  );
}

function SectionLabel({ children, style = {} }) {
  return (
    <div style={{ fontSize: 10, color: "#55555e", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, ...style }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ padding: "52px 0", textAlign: "center", color: "#55555e", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      loading…
    </div>
  );
}

function RatingGrid({ userRating, setUserRating, columns = 3, className = "" }) {
  return (
    <div className={className} style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 6, marginBottom: 10 }}>
      {ratingLabels.map(r => (
        <div key={r.key} onClick={() => setUserRating(r.key)}
          style={{
            background: userRating === r.key ? "#FF5C35" : "#161618",
            border: `0.5px solid ${userRating === r.key ? "#FF5C35" : "#252528"}`,
            borderRadius: 8, padding: "8px 4px", textAlign: "center", cursor: "pointer",
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 16, marginBottom: 3 }}>{r.icon}</div>
          <div style={{ fontSize: 9, fontWeight: 700, color: userRating === r.key ? "#fff" : "#888", fontFamily: "'Bricolage Grotesque', sans-serif" }}>{r.label}</div>
          <div style={{ fontSize: 8, color: userRating === r.key ? "rgba(255,255,255,0.5)" : "#55555e" }}>{r.nepali}</div>
        </div>
      ))}
    </div>
  );
}

function SubmitRatingButton({ userRating, submitted, onSubmit }) {
  return (
    <button
      onClick={() => { if (userRating && !submitted) onSubmit(); }}
      style={{
        width: "100%", padding: 9, borderRadius: 20,
        background: submitted ? "#0e2010" : "#FF5C35",
        color: submitted ? "#4a9a6a" : "#fff",
        border: "none", fontSize: 12, fontWeight: 700,
        fontFamily: "'Bricolage Grotesque', sans-serif",
        cursor: userRating ? "pointer" : "default",
        opacity: !userRating && !submitted ? 0.45 : 1,
        transition: "all 0.2s",
      }}
    >
      {submitted ? "✓ rating submitted!" : "submit rating"}
    </button>
  );
}

function PersonAvatar({ path, name, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const initials = name ? name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "?";
  if (!path || failed) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", background: "#1c1c1f", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.35), fontWeight: 800, color: "#3a3a46", fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        {initials}
      </div>
    );
  }
  return (
    <img src={`https://image.tmdb.org/t/p/w185${path}`} alt={name}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      onError={() => setFailed(true)}
    />
  );
}

function CastPortrait({ path, name, height = 148 }) {
  const [failed, setFailed] = useState(false);
  const initials = name ? name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "?";
  return (
    <div style={{ height, background: "#161618", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {!path || failed ? (
        <span style={{ fontSize: Math.round(height * 0.28), fontWeight: 800, color: "#2a2a32", fontFamily: "'Bricolage Grotesque', sans-serif" }}>{initials}</span>
      ) : (
        <img src={`https://image.tmdb.org/t/p/w185${path}`} alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab({ show, providers, trailer, userRating, setUserRating, submitted, onRatingSubmit }) {
  const rd = show.ratings_breakdown || {};
  const hasStream = providers?.stream?.length || providers?.rent?.length || providers?.buy?.length;
  const ytTrailer = trailer?.key ? `https://www.youtube.com/watch?v=${trailer.key}` : null;
  const ytSearch  = `https://www.youtube.com/results?search_query=${encodeURIComponent((show.name || "") + " trailer")}`;

  return (
    <div>
      <div className="overview-grid" style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 32 }}>

        {/* Main column */}
        <div>
          {show.synopsis && (
            <>
              <SectionLabel>synopsis</SectionLabel>
              <p style={{ fontSize: 14, color: "#6a6a78", lineHeight: 1.85, marginBottom: 32, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{show.synopsis}</p>
            </>
          )}

          {trailer && (
            <>
              <SectionLabel>trailer</SectionLabel>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 14, overflow: "hidden", marginBottom: 32, background: "#111", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                <iframe
                  src={`https://www.youtube.com/embed/${trailer.key}?rel=0`}
                  title="Trailer"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                />
              </div>
            </>
          )}

          {Object.values(rd).some(v => v > 0) && (
            <>
              <SectionLabel>community ratings</SectionLabel>
              <div style={{ marginBottom: 32 }}>
                {ratingLabels.map(r => {
                  const pct = rd[r.key] || 0;
                  return (
                    <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                      <div style={{ fontSize: 11, color: "#55555e", minWidth: 90, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{r.icon} {r.label}</div>
                      <div style={{ flex: 1, height: 4, background: "#1c1c1f", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: pct > 30 ? "#FF5C35" : "#3a3a42", borderRadius: 2, transition: "width 0.7s ease" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#55555e", minWidth: 30, textAlign: "right" }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Where to Watch */}
          <div style={{ borderTop: "0.5px solid #1c1c1f", paddingTop: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>📺</span>
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#e8e8ee" }}>Where to Watch</h3>
            </div>

            {/* YouTube buttons */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
              {ytTrailer && (
                <a href={ytTrailer} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <button style={{ background: "#FF0000", color: "#fff", border: "none", padding: "9px 20px", borderRadius: 22, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    ▶ Watch Trailer
                  </button>
                </a>
              )}
              <a href={ytSearch} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <button style={{ background: "#161618", color: "#c0c0c0", border: "0.5px solid #2e2e34", padding: "9px 20px", borderRadius: 22, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  🔍 Search on YouTube
                </button>
              </a>
              {providers?.link && (
                <a href={providers.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <button style={{ background: "#161618", color: "#c0c0c0", border: "0.5px solid #2e2e34", padding: "9px 20px", borderRadius: 22, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    all options →
                  </button>
                </a>
              )}
            </div>

            {hasStream ? (
              <div>
                {[
                  { label: "Stream", key: "stream", color: "#4a9a6a" },
                  { label: "Rent",   key: "rent",   color: "#9a8040" },
                  { label: "Buy",    key: "buy",    color: "#55555e" },
                ].filter(g => providers[g.key]?.length > 0).map(g => (
                  <div key={g.key} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 10, color: g.color, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>{g.label}</div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                      {providers[g.key].map((p, i) => <ProviderLogo key={i} p={p} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 16 }}>
                No streaming providers found for your region.
              </p>
            )}
            {show.language === "ne" && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, color: "#55555e", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>Nepali platforms</div>
                <div style={{ display: "flex", gap: 12 }}>
                  {NEPALI_PLATFORMS.map(p => (
                    <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <div style={{ padding: "8px 16px", background: "#0f0f12", border: "0.5px solid #252528", borderRadius: 10, fontSize: 12, fontWeight: 700, color: p.color, fontFamily: "'Bricolage Grotesque', sans-serif", cursor: "pointer" }}>
                        {p.name} →
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 16, marginBottom: 12 }}>
            <SectionLabel>your rating</SectionLabel>
            <RatingGrid userRating={userRating} setUserRating={setUserRating} columns={3} />
            <SubmitRatingButton userRating={userRating} submitted={submitted} onSubmit={onRatingSubmit} />
          </div>
          <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 16 }}>
            <SectionLabel>show info</SectionLabel>
            {[
              ["seasons",  show.number_of_seasons  ? `${show.number_of_seasons} season${show.number_of_seasons !== 1 ? "s" : ""}` : null],
              ["episodes", show.number_of_episodes ? `${show.number_of_episodes} episodes` : null],
              ["status",   show.status],
              ["year",     show.year],
              ["language", show.language?.toUpperCase()],
            ].filter(([, v]) => v != null).map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "7px 0", borderBottom: "0.5px solid #1c1c1f" }}>
                <span style={{ color: "#55555e" }}>{k}</span>
                <span style={{ color: "#c0c0c0", fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Reviews ─────────────────────────────────────────────────────────────

function ReviewsTab({ show, userRating, setUserRating, submitted, onRatingSubmit }) {
  const rd = show.ratings_breakdown || {};
  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 20 }}>
        {show.total_ratings > 0 ? (
          <>
            <SectionLabel>GuffGaff community rating</SectionLabel>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 44, fontWeight: 800, background: "linear-gradient(135deg, #FF5C35, #ff8a68)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", lineHeight: 1 }}>
                {show.guffgaff_score}
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#c0c0c0", fontWeight: 600 }}>community score</div>
                <div style={{ fontSize: 11, color: "#55555e", marginTop: 2 }}>{show.total_ratings.toLocaleString()} ratings on GuffGaff</div>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              {ratingLabels.map(r => {
                const pct = rd[r.key] || 0;
                return (
                  <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: "#55555e", minWidth: 90 }}>{r.icon} {r.label}</div>
                    <div style={{ flex: 1, height: 4, background: "#1c1c1f", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "#FF5C35", borderRadius: 2, transition: "width 0.6s ease" }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#55555e", minWidth: 30, textAlign: "right" }}>{pct}%</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", paddingBottom: 20 }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>📺</div>
            <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 700, color: "#c0c0c0", marginBottom: 6 }}>
              Be the first to rate this on GuffGaff
            </div>
            <div style={{ fontSize: 12, color: "#55555e", marginBottom: 20 }}>Your rating shapes the community score.</div>
          </div>
        )}
        <div style={{ borderTop: "0.5px solid #1c1c1f", paddingTop: 18 }}>
          <SectionLabel>rate this show</SectionLabel>
          <RatingGrid userRating={userRating} setUserRating={setUserRating} columns={6} className="rating-grid-6" />
          <SubmitRatingButton userRating={userRating} submitted={submitted} onSubmit={onRatingSubmit} />
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Discussions ─────────────────────────────────────────────────────────

function GuffForm({ showId, onSuccess, onCancel }) {
  const [step, setStep]         = useState("pick");
  const [question, setQuestion] = useState("");
  const [sideA, setSideA]       = useState("");
  const [sideB, setSideB]       = useState("");
  const [quote, setQuote]       = useState("");
  const [sceneTag, setSceneTag] = useState("");
  const [error, setError]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  const token    = localStorage.getItem("guffgaff_token");
  const typeInfo = GUFF_TYPES.find(t => t.key === step);

  async function handleSubmit() {
    if (!question.trim()) { setError("question is required"); return; }
    if (!token) { setError("you need to sign in to post"); return; }
    setSubmitting(true);
    setError("");
    try {
      await createDiscussion({
        type: step, question: question.trim(), show_id: parseInt(showId),
        ...(step === "battle" && { side_a: sideA.trim(), side_b: sideB.trim() }),
        ...(step === "scene"  && { quote: quote.trim(), scene_tag: sceneTag.trim() }),
      }, token);
      onSuccess();
    } catch {
      setError("failed to post — try again");
      setSubmitting(false);
    }
  }

  const iStyle = { width: "100%", padding: "9px 12px", background: "#161618", border: "0.5px solid #252528", borderRadius: 8, color: "#c0c0c0", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 14, padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, color: "#c0c0c0", fontSize: 14 }}>
          {step === "pick" ? "pick a guff type" : `${typeInfo?.icon} ${typeInfo?.label}`}
        </div>
        <button onClick={onCancel} style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
      </div>
      {step === "pick" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {GUFF_TYPES.map(t => (
            <div key={t.key} onClick={() => setStep(t.key)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#161618", border: "0.5px solid #252528", borderRadius: 10, cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,92,53,0.4)"}
              onMouseLeave={e => e.currentTarget.style.borderColor = "#252528"}
            >
              <span style={{ fontSize: 20 }}>{t.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#c0c0c0", fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t.label}</div>
                <div style={{ fontSize: 11, color: "#55555e", marginTop: 1 }}>{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <textarea value={question} onChange={e => setQuestion(e.target.value)} placeholder="What's the guff?" style={{ ...iStyle, minHeight: 80, resize: "vertical", marginBottom: 10 }} />
          {step === "battle" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <input value={sideA} onChange={e => setSideA(e.target.value)} placeholder="Side A" style={iStyle} />
              <input value={sideB} onChange={e => setSideB(e.target.value)} placeholder="Side B" style={iStyle} />
            </div>
          )}
          {step === "scene" && (
            <>
              <input value={sceneTag} onChange={e => setSceneTag(e.target.value)} placeholder="scene tag (e.g. episode 5 · no spoilers)" style={{ ...iStyle, marginBottom: 8 }} />
              <textarea value={quote} onChange={e => setQuote(e.target.value)} placeholder="memorable quote…" style={{ ...iStyle, minHeight: 60, resize: "vertical", marginBottom: 10 }} />
            </>
          )}
          {error && <div style={{ fontSize: 12, color: "#FF5C35", marginBottom: 10 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSubmit} disabled={submitting} style={{ background: "#FF5C35", color: "#fff", border: "none", padding: "9px 22px", borderRadius: 20, fontSize: 12, fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}>
              {submitting ? "posting…" : "post guff"}
            </button>
            <button onClick={() => setStep("pick")} style={{ background: "transparent", color: "#55555e", border: "0.5px solid #252528", padding: "9px 16px", borderRadius: 20, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              ← change type
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function DiscussionCard({ d, onRefresh }) {
  const { currentUser, token } = useAuth();
  const isOwner = currentUser?.id === d.user_id;

  const [editing,       setEditing]       = useState(false);
  const [editQuestion,  setEditQuestion]  = useState(d.question);
  const [editSideA,     setEditSideA]     = useState(d.side_a || "");
  const [editSideB,     setEditSideB]     = useState(d.side_b || "");
  const [saving,        setSaving]        = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const typeColor = { battle: "#FF5C35", hottake: "#FF5C35", scene: "#8b7dd8", predict: "#4a9a6a", spoiler: "#9a4a4a" };
  const typeIcon  = { battle: "⚔", hottake: "🔥", scene: "🎬", predict: "🎯", spoiler: "💀" };
  const totalBattle = (d.votes?.a || 0) + (d.votes?.b || 0);

  async function handleSave() {
    if (!editQuestion.trim() || saving) return;
    setSaving(true);
    try {
      await editDiscussion(d.id, {
        question: editQuestion.trim(),
        ...(d.type === "battle" && { side_a: editSideA.trim(), side_b: editSideB.trim() }),
      }, token);
      setEditing(false);
      onRefresh();
    } catch {}
    setSaving(false);
  }

  async function handleDelete() {
    try {
      await deleteDiscussion(d.id, token);
      onRefresh();
    } catch {}
  }

  const iStyle = { width: "100%", padding: "8px 10px", background: "#0f0f12", border: "0.5px solid #2e2e34", borderRadius: 8, color: "#c0c0c0", fontSize: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none", boxSizing: "border-box", marginBottom: 8, resize: "vertical" };

  return (
    <div style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#1c1c1f", color: typeColor[d.type] || "#55555e", border: "0.5px solid #252528" }}>
          {typeIcon[d.type]} {d.type}
        </div>
        {d.user && <div style={{ fontSize: 11, color: "#55555e" }}>{d.user.username}{d.user.location ? ` · ${d.user.location}` : ""}</div>}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, color: "#333" }}>{new Date(d.created_at).toLocaleDateString()}</span>
          {isOwner && !editing && (
            <>
              <button onClick={() => { setEditing(true); setConfirmDelete(false); }}
                style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 11, padding: "2px 6px", borderRadius: 6 }}
                onMouseEnter={e => e.currentTarget.style.color = "#c0c0c0"}
                onMouseLeave={e => e.currentTarget.style.color = "#55555e"}
              >edit</button>
              {confirmDelete ? (
                <>
                  <button onClick={handleDelete} style={{ background: "none", border: "none", color: "#e05a5a", cursor: "pointer", fontSize: 11, padding: "2px 6px" }}>yes, delete</button>
                  <button onClick={() => setConfirmDelete(false)} style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 11, padding: "2px 6px" }}>cancel</button>
                </>
              ) : (
                <button onClick={() => setConfirmDelete(true)}
                  style={{ background: "none", border: "none", color: "#55555e", cursor: "pointer", fontSize: 11, padding: "2px 6px", borderRadius: 6 }}
                  onMouseEnter={e => e.currentTarget.style.color = "#e05a5a"}
                  onMouseLeave={e => e.currentTarget.style.color = "#55555e"}
                >delete</button>
              )}
            </>
          )}
        </div>
      </div>

      {editing ? (
        <>
          <textarea value={editQuestion} onChange={e => setEditQuestion(e.target.value)} style={{ ...iStyle, minHeight: 72 }} />
          {d.type === "battle" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <input value={editSideA} onChange={e => setEditSideA(e.target.value)} placeholder="Side A" style={{ ...iStyle, marginBottom: 0, resize: "none" }} />
              <input value={editSideB} onChange={e => setEditSideB(e.target.value)} placeholder="Side B" style={{ ...iStyle, marginBottom: 0, resize: "none" }} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={handleSave} disabled={saving} style={{ background: "#FF5C35", color: "#fff", border: "none", padding: "7px 18px", borderRadius: 18, fontSize: 12, fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}>
              {saving ? "saving…" : "save"}
            </button>
            <button onClick={() => { setEditing(false); setEditQuestion(d.question); setEditSideA(d.side_a || ""); setEditSideB(d.side_b || ""); }}
              style={{ background: "transparent", color: "#55555e", border: "0.5px solid #252528", padding: "7px 14px", borderRadius: 18, fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: "#c0c0c0", lineHeight: 1.55, marginBottom: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{d.question}</div>
          {d.type === "battle" && d.side_a && d.side_b && (
            <>
              <div style={{ height: 5, borderRadius: 3, overflow: "hidden", display: "flex", marginBottom: 6, background: "#1c1c1f" }}>
                <div style={{ width: `${d.votes?.pct_a || 50}%`, background: "#FF5C35" }} />
                <div style={{ width: `${d.votes?.pct_b || 50}%`, background: "#8b7dd8" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span style={{ color: "#FF5C35" }}>{d.side_a} · {d.votes?.pct_a || 50}%</span>
                <span style={{ color: "#8b7dd8" }}>{d.side_b} · {d.votes?.pct_b || 50}%</span>
              </div>
              {totalBattle > 0 && <div style={{ fontSize: 10, color: "#333", marginTop: 4 }}>{totalBattle} voted</div>}
            </>
          )}
          {d.type === "scene" && d.quote && (
            <div style={{ fontSize: 12, color: "#666", fontStyle: "italic", borderLeft: "2px solid #333", paddingLeft: 10, marginTop: 4 }}>"{d.quote}"</div>
          )}
        </>
      )}
    </div>
  );
}

function DiscussionsTab({ showId, discussions, loading, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  return (
    <div style={{ maxWidth: 700 }}>
      {!showForm && (
        <div style={{ marginBottom: 20 }}>
          <button onClick={() => setShowForm(true)} style={{ background: "#FF5C35", color: "#fff", border: "none", padding: "10px 24px", borderRadius: 22, fontSize: 13, fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", cursor: "pointer" }}>
            + start a guff
          </button>
        </div>
      )}
      {showForm && <GuffForm showId={showId} onSuccess={() => { setShowForm(false); onRefresh(); }} onCancel={() => setShowForm(false)} />}
      {loading ? <Spinner /> : discussions.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#55555e", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>no discussions yet — start one above!</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {discussions.map(d => <DiscussionCard key={d.id} d={d} onRefresh={onRefresh} />)}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Cast & Crew ─────────────────────────────────────────────────────────

function CastTab({ credits, loading }) {
  if (loading) return <Spinner />;
  if (!credits) return <div style={{ padding: "40px 0", textAlign: "center", color: "#55555e", fontSize: 13 }}>no cast info available</div>;

  const crewRoles = [
    { role: "Creator",     match: c => c.job === "Creator"                 },
    { role: "Director",    match: c => c.job === "Director"                },
    { role: "Producer",    match: c => c.job === "Executive Producer"      },
    { role: "Music",       match: c => c.job === "Original Music Composer" },
  ];
  const keyCrew = crewRoles
    .map(({ role, match }) => ({ role, person: (credits.crew || []).find(match) }))
    .filter(x => x.person);

  return (
    <div>
      {keyCrew.length > 0 && (
        <>
          <SectionLabel>crew</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginBottom: 36 }}>
            {keyCrew.map(({ role, person }) => (
              <div key={role} style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                <PersonAvatar path={person.profile_path} name={person.name} size={44} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#c0c0c0", fontFamily: "'Bricolage Grotesque', sans-serif" }}>{person.name}</div>
                  <div style={{ fontSize: 10, color: "#55555e", marginTop: 3 }}>{role}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <SectionLabel>cast</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12 }}>
        {(credits.cast || []).slice(0, 24).map(person => (
          <div key={person.id} style={{ background: "#111113", border: "0.5px solid #252528", borderRadius: 10, overflow: "hidden" }}>
            <CastPortrait path={person.profile_path} name={person.name} height={148} />
            <div style={{ padding: "8px 10px 10px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#c0c0c0", fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: 2 }}>{person.name}</div>
              {person.character && (
                <div style={{ fontSize: 10, color: "#55555e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.character}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: Similar ─────────────────────────────────────────────────────────────

function SimilarTab({ similar, loading, dbShows, onNavigate }) {
  if (loading) return <Spinner />;
  if (similar.length === 0) return <div style={{ padding: "40px 0", textAlign: "center", color: "#55555e", fontSize: 13 }}>no similar shows found</div>;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 14 }}>
      {similar.map(s => {
        const ourId = dbShows[s.id];
        const poster = s.poster_path ? `https://image.tmdb.org/t/p/w500${s.poster_path}` : null;
        return (
          <div key={s.id} onClick={() => ourId && onNavigate(`/show/${ourId}`)}
            style={{ background: "#0f0f12", border: "0.5px solid #222226", borderRadius: 14, overflow: "hidden", cursor: ourId ? "pointer" : "default", opacity: ourId ? 1 : 0.52, transition: "border-color 0.2s, transform 0.2s" }}
            onMouseEnter={e => { if (!ourId) return; e.currentTarget.style.borderColor = "rgba(255,92,53,0.5)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#222226"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ height: 110, background: "#1a1a1f", overflow: "hidden", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {poster ? <img src={poster} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.currentTarget.style.display = "none"; }} /> : <span style={{ fontSize: 28, opacity: 0.2 }}>📺</span>}
              {!ourId && <div style={{ position: "absolute", bottom: 5, right: 5, fontSize: 9, background: "rgba(11,11,12,0.88)", color: "#55555e", border: "0.5px solid #252528", borderRadius: 6, padding: "2px 6px" }}>not in GuffGaff</div>}
            </div>
            <div style={{ padding: "10px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#ebebee", fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: 3, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.name}</div>
              <div style={{ fontSize: 10, color: "#52525e" }}>{s.first_air_date?.slice(0, 4) || ""}{s.vote_average ? ` · ★ ${s.vote_average.toFixed(1)}` : ""}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ShowDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { token, showAuthGate } = useAuth();

  const [show,      setShow]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [providers, setProviders] = useState(null);
  const [toast,     setToast]     = useState("");
  const [listOpen,  setListOpen]  = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [userRating, setUserRating] = useState(null);
  const [submitted,  setSubmitted]  = useState(false);
  const [trailer,    setTrailer]    = useState(null);

  const [discussions,        setDiscussions]        = useState([]);
  const [discussionsLoading, setDiscussionsLoading] = useState(false);
  const [discussionsFetched, setDiscussionsFetched] = useState(false);

  const [credits,        setCredits]        = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsFetched, setCreditsFetched] = useState(false);

  const [similar,        setSimilar]        = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarFetched, setSimilarFetched] = useState(false);
  const [dbShows,        setDbShows]        = useState({});

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3000); }

  async function handleRatingSubmit() {
    if (!userRating || submitted) return;
    if (!token) { showAuthGate("rate this show"); return; }
    try {
      const result = await submitShowRating(parseInt(id), userRating, token);
      if (result.score !== undefined) {
        setShow(s => ({ ...s, guffgaff_score: result.score, total_ratings: result.total, ratings_breakdown: result.breakdown }));
      }
    } catch {}
    setSubmitted(true);
  }

  // Load show + providers
  useEffect(() => {
    setLoading(true);
    setActiveTab("overview");
    setUserRating(null);
    setSubmitted(false);
    setTrailer(null);
    setDiscussions([]);  setDiscussionsFetched(false);
    setCredits(null);    setCreditsFetched(false);
    setSimilar([]);      setSimilarFetched(false);
    getShow(id)
      .then(data => { setShow(data); return getShowWatchProviders(id); })
      .then(p => setProviders(p))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch trailer eagerly
  useEffect(() => {
    if (!show?.tmdb_id || trailer !== null) return;
    tmdbGet(`/tv/${show.tmdb_id}/videos`).then(data => {
      const results = data?.results || [];
      const t = results.find(v => v.type === "Trailer" && v.site === "YouTube")
             || results.find(v => v.type === "Teaser"  && v.site === "YouTube")
             || results.find(v => v.type === "Opening Credits" && v.site === "YouTube");
      setTrailer(t ?? false);
    });
  }, [show, trailer]);

  // Lazy fetch per tab
  useEffect(() => {
    if (!show?.tmdb_id) return;

    if (activeTab === "discussions" && !discussionsFetched) {
      setDiscussionsFetched(true);
      setDiscussionsLoading(true);
      getDiscussions({ show_id: id })
        .then(data => setDiscussions(data.discussions || []))
        .catch(() => setDiscussions([]))
        .finally(() => setDiscussionsLoading(false));
    }

    if (activeTab === "cast" && !creditsFetched) {
      setCreditsFetched(true);
      setCreditsLoading(true);
      tmdbGet(`/tv/${show.tmdb_id}/credits`)
        .then(data => setCredits(data))
        .finally(() => setCreditsLoading(false));
    }

    if (activeTab === "similar" && !similarFetched) {
      setSimilarFetched(true);
      setSimilarLoading(true);
      Promise.all([
        tmdbGet(`/tv/${show.tmdb_id}/similar`),
        fetch("https://gufgaff.com/api/shows?per_page=200").then(r => r.json()).catch(() => ({ shows: [] })),
      ]).then(([simData, allData]) => {
        setSimilar((simData?.results || []).slice(0, 12));
        const map = {};
        for (const s of (allData?.shows || [])) { if (s.tmdb_id) map[s.tmdb_id] = s.id; }
        setDbShows(map);
      }).catch(() => {}).finally(() => setSimilarLoading(false));
    }
  }, [activeTab, show, discussionsFetched, creditsFetched, similarFetched, id]);

  function refreshDiscussions() {
    setDiscussionsLoading(true);
    getDiscussions({ show_id: id })
      .then(data => setDiscussions(data.discussions || []))
      .catch(() => {})
      .finally(() => setDiscussionsLoading(false));
  }

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }}>loading…</div>
  );
  if (!show) return (
    <div style={{ padding: 40, textAlign: "center", color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      Show not found.{" "}
      <button onClick={() => navigate(-1)} style={{ color: "#FF5C35", background: "none", border: "none", cursor: "pointer" }}>go back</button>
    </div>
  );

  const genres = show.genres || [];
  const statusColor = {
    "Returning Series": "#4a9a6a",
    "Ended":            "#9a4a4a",
    "Canceled":         "#9a4a4a",
    "In Production":    "#9a8040",
  }[show.status] || "#55555e";

  return (
    <div style={{ minHeight: "100vh", background: "#0b0b0c" }}>
      {toast && (
        <div style={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", background: "#1c1c20", border: "0.5px solid #3a3a44", borderRadius: 24, padding: "10px 22px", color: "#c0c0c0", fontSize: 13, zIndex: 9999, boxShadow: "0 4px 24px rgba(0,0,0,0.5)", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" }}>
          {toast}
        </div>
      )}

      {/* ── Hero ── */}
      <div className="detail-hero" style={{ position: "relative", width: "100%", height: 500, background: "#08080a", overflow: "visible" }}>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          {(show.backdrop_url || show.poster_url) && (
            <img src={show.backdrop_url || show.poster_url} alt="" aria-hidden="true"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", filter: "brightness(0.45)" }}
            />
          )}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(8,8,10,0.3) 0%, rgba(8,8,10,0.15) 40%, rgba(8,8,10,0.75) 80%, #0b0b0c 100%)" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(8,8,10,0.6) 0%, transparent 50%)" }} />
        </div>

        <div className="detail-hero-inner" style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%", padding: "0 20px" }}>
            <div onClick={() => navigate(-1)} style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 22, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", userSelect: "none" }}>
              ← {show.is_anime ? "anime" : "series"}
            </div>

            <div className="detail-hero-row" style={{ display: "flex", gap: 28, alignItems: "flex-end", paddingBottom: 56 }}>
              {/* Poster */}
              <div className="detail-hero-poster" style={{ width: 130, height: 194, flexShrink: 0, borderRadius: 12, overflow: "hidden", boxShadow: "0 16px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.07)" }}>
                {show.poster_url ? (
                  <img src={show.poster_url} alt={show.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: "#1a1a1f", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, opacity: 0.2 }}>📺</div>
                )}
              </div>

              {/* Info */}
              <div style={{ flex: 1, paddingBottom: 6 }}>
                {show.is_anime && (
                  <div style={{ fontSize: 10, color: "#9a8040", background: "rgba(154,128,64,0.12)", border: "0.5px solid rgba(154,128,64,0.3)", borderRadius: 8, padding: "2px 10px", display: "inline-block", marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    ANIME
                  </div>
                )}
                <h1 className="detail-hero-title" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 38, fontWeight: 800, color: "#fff", marginBottom: 10, letterSpacing: "-0.8px", lineHeight: 1.08, textShadow: "0 2px 24px rgba(0,0,0,0.6)" }}>
                  {show.name}
                </h1>
                {show.original_name && show.original_name !== show.name && (
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 10, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{show.original_name}</div>
                )}

                <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
                  {show.year && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.42)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{show.year} ·</span>}
                  {show.status && (
                    <span style={{ fontSize: 10, padding: "2px 9px", borderRadius: 8, background: `${statusColor}18`, color: statusColor, border: `0.5px solid ${statusColor}44`, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {show.status}
                    </span>
                  )}
                  {genres.map(g => (
                    <span key={g} style={{ fontSize: 10, padding: "2px 10px", borderRadius: 10, background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.62)", border: "0.5px solid rgba(255,255,255,0.11)" }}>
                      {g}
                    </span>
                  ))}
                </div>

                {/* Score row */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 22 }}>
                  {show.guffgaff_score && (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5, paddingRight: 20 }}>
                      <span className="detail-hero-guff-score" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 44, fontWeight: 800, lineHeight: 1, background: "linear-gradient(135deg, #FF5C35, #ff8a68)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                        {show.guffgaff_score}
                      </span>
                      <div style={{ paddingBottom: 3 }}>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.28)", lineHeight: 1 }}>/10</div>
                        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.22)", marginTop: 2, whiteSpace: "nowrap" }}>GuffGaff score</div>
                      </div>
                    </div>
                  )}
                  {show.score && (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5, paddingLeft: show.guffgaff_score ? 20 : 0, borderLeft: show.guffgaff_score ? "0.5px solid rgba(255,255,255,0.1)" : "none" }}>
                      <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: show.guffgaff_score ? 26 : 44, fontWeight: 800, lineHeight: 1, color: "rgba(255,255,255,0.55)" }}>
                        {show.score}
                      </span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.28)" }}>/10 TMDB</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {trailer?.key && (
                    <a href={`https://www.youtube.com/watch?v=${trailer.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <button style={{ background: "#FF0000", color: "#fff", padding: "10px 26px", borderRadius: 22, fontSize: 13, fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", border: "none", cursor: "pointer" }}>
                        ▶ Watch Trailer
                      </button>
                    </a>
                  )}
                  <button
                    onClick={() => setActiveTab("reviews")}
                    style={{ background: trailer?.key ? "rgba(255,255,255,0.06)" : "#FF5C35", color: trailer?.key ? "rgba(255,255,255,0.68)" : "#fff", padding: "10px 26px", borderRadius: 22, fontSize: 13, fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", border: trailer?.key ? "0.5px solid rgba(255,255,255,0.11)" : "none", cursor: "pointer", boxShadow: trailer?.key ? "none" : "0 4px 22px rgba(255,92,53,0.45)" }}
                  >
                    rate this show
                  </button>
                  <div style={{ position: "relative" }}>
                    <button
                      onClick={() => setListOpen(v => !v)}
                      style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.65)", border: "0.5px solid rgba(255,255,255,0.12)", padding: "10px 22px", borderRadius: 22, fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif", cursor: "pointer" }}
                    >
                      + add to list
                    </button>
                    {listOpen && (
                      <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 200, background: "#161618", border: "0.5px solid #2e2e34", borderRadius: 12, padding: "6px 0", minWidth: 200, boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
                        {LIST_ITEMS.map(list => {
                          const inList = isInList(list.id, show?.id);
                          return (
                            <div key={list.id}
                              onClick={() => {
                                if (inList) { removeFromList(list.id, show.id); showToast(`Removed from ${list.label}`); }
                                else { addToList(list.id, { id: show.id, title: show.name, poster_url: show.poster_url, year: show.year, score: show.score }); showToast(`Added to ${list.label}`); }
                                setListOpen(false);
                              }}
                              style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer", color: inList ? "#FF5C35" : "#c0c0c0", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#1c1c1f"}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            >
                              <span style={{ fontSize: 15 }}>{list.icon}</span>
                              <span style={{ flex: 1 }}>{list.label}</span>
                              {inList && <span style={{ fontSize: 12, color: "#FF5C35" }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab bar anchored to hero bottom */}
          <div style={{ borderTop: "0.5px solid rgba(255,255,255,0.06)", background: "rgba(8,8,10,0.92)", backdropFilter: "blur(12px)" }}>
            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
              <TabBar active={activeTab} onSelect={setActiveTab} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 20px" }}>
        {activeTab === "overview" && (
          <OverviewTab
            show={show} providers={providers} trailer={trailer}
            userRating={userRating} setUserRating={setUserRating}
            submitted={submitted} onRatingSubmit={handleRatingSubmit}
          />
        )}
        {activeTab === "reviews" && (
          <ReviewsTab
            show={show}
            userRating={userRating} setUserRating={setUserRating}
            submitted={submitted} onRatingSubmit={handleRatingSubmit}
          />
        )}
        {activeTab === "discussions" && (
          <DiscussionsTab showId={id} discussions={discussions} loading={discussionsLoading} onRefresh={refreshDiscussions} />
        )}
        {activeTab === "cast" && (
          <CastTab credits={credits} loading={creditsLoading} />
        )}
        {activeTab === "similar" && (
          <SimilarTab similar={similar} loading={similarLoading} dbShows={dbShows} onNavigate={navigate} />
        )}
      </div>
    </div>
  );
}
